/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import { useCallback, useEffect } from "react";
import { StopPlaceActions } from "../../../../actions";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { UseEditStopPageReturn } from "../types";
import { useStopPlaceCRUD } from "./useStopPlaceCRUD";
import { useStopPlaceDialogs } from "./useStopPlaceDialogs";
import { useStopPlaceForm } from "./useStopPlaceForm";
import { useStopPlaceParking } from "./useStopPlaceParking";
import { useStopPlaceQuays } from "./useStopPlaceQuays";
import { useStopPlaceState } from "./useStopPlaceState";
import { useStopPlaceVersions } from "./useStopPlaceVersions";

/**
 * Orchestrator hook for the modern EditStopPage
 * Combines 6 focused sub-hooks into a unified interface
 */
export const useEditStopPage = (): UseEditStopPageReturn => {
  const dispatch = useAppDispatch();

  // 1. State (Redux selectors + permissions)
  const {
    stopPlace,
    originalStopPlace,
    isModified,
    activeMap,
    canEdit,
    canDelete,
    terminateStopDialogOpen,
  } = useStopPlaceState();

  // 1b. Lazy version history — fetched only on first dialog open per stop
  const { versions, versionsLoading, fetchVersions } = useStopPlaceVersions(
    stopPlace?.id,
  );

  // Promote newStop → current when a freshly placed stop first loads.
  // This ensures all field-change reducers (CHANGED_STOP_NAME, etc.) that
  // write to `current` have a valid base object to spread into.
  const hasCurrentInRedux = useAppSelector(
    (state) =>
      state.stopPlace.current !== null && state.stopPlace.current !== undefined,
  );
  useEffect(() => {
    if (stopPlace?.isNewStop && !hasCurrentInRedux) {
      dispatch(StopPlaceActions.useNewStopAsCurrent());
    }
    // Only run once on mount — after promotion, hasCurrentInRedux becomes true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Dialog state management (local boolean flags)
  const {
    confirmSaveDialogOpen,
    confirmGoBackOpen,
    confirmUndoOpen,
    deleteQuayDialogOpen,
    deleteParkingDialogOpen,
    requiredFieldsMissingOpen,
    tagsDialogOpen,
    handleOpenSaveDialog,
    handleCloseSaveDialog,
    handleOpenGoBackDialog,
    handleCloseGoBackDialog,
    handleOpenUndoDialog,
    handleCloseUndoDialog,
    handleOpenDeleteQuayDialog,
    handleCloseDeleteQuayDialog,
    handleOpenDeleteParkingDialog,
    handleCloseDeleteParkingDialog,
    handleOpenRequiredFieldsMissing,
    handleCloseRequiredFieldsMissing,
    handleOpenTagsDialog,
    handleCloseTagsDialog,
    altNamesDialogOpen,
    handleOpenAltNamesDialog,
    handleCloseAltNamesDialog,
    versionsDialogOpen,
    handleOpenVersionsDialog: openVersionsDialogRaw,
    handleCloseVersionsDialog,
  } = useStopPlaceDialogs();

  // 3. CRUD (save, undo, go back, terminate)
  const {
    handleSave,
    handleAllowUserToGoBack: handleGoBackCheck,
    handleGoBack,
    handleUndo,
    handleOpenTerminateDialog,
    handleCloseTerminateDialog,
    handleTerminate,
  } = useStopPlaceCRUD(
    stopPlace,
    isModified,
    activeMap,
    handleCloseSaveDialog,
    handleCloseGoBackDialog,
    handleCloseUndoDialog,
    handleOpenRequiredFieldsMissing,
  );

  // 4. Form handlers (name, description, type, tags)
  const {
    handleNameChange,
    handleDescriptionChange,
    handleTypeChange,
    handleSubmodeChange,
    handleWeightingChange,
    handleAddTag,
    handleGetTags,
    handleRemoveTag,
    handleFindTagByName,
  } = useStopPlaceForm();

  // 5. Quay handlers
  const {
    handleDeleteQuay,
    handleConfirmDeleteQuay,
    pendingDeleteQuayId,
    handleQuayPublicCodeChange,
    handleQuayPrivateCodeChange,
    handleQuayDescriptionChange,
    handleQuayCompassBearingChange,
  } = useStopPlaceQuays(
    stopPlace,
    handleOpenDeleteQuayDialog,
    handleCloseDeleteQuayDialog,
  );

  // 6. Parking handlers
  const {
    handleDeleteParking,
    handleConfirmDeleteParking,
    handleParkingNameChange,
    handleParkingTypeChange,
    handleParkingCapacityChange,
  } = useStopPlaceParking(
    stopPlace,
    handleOpenDeleteParkingDialog,
    handleCloseDeleteParkingDialog,
  );

  // Wrapper: open go-back dialog if modified, else navigate directly
  const handleAllowUserToGoBack = useCallback(() => {
    const shouldShowDialog = handleGoBackCheck();
    if (shouldShowDialog) {
      handleOpenGoBackDialog();
    }
  }, [handleGoBackCheck, handleOpenGoBackDialog]);

  const handleCancelGoBack = useCallback(() => {
    handleCloseGoBackDialog();
  }, [handleCloseGoBackDialog]);

  // Trigger the lazy versions fetch and open the dialog in one action
  const handleOpenVersionsDialogWithFetch = useCallback(() => {
    fetchVersions();
    openVersionsDialogRaw();
  }, [fetchVersions, openVersionsDialogRaw]);

  return {
    stopPlace,
    originalStopPlace,
    isModified,
    canEdit,
    canDelete,

    versions,
    versionsLoading,

    confirmSaveDialogOpen,
    confirmGoBackOpen,
    confirmUndoOpen,
    terminateStopDialogOpen,
    deleteQuayDialogOpen,
    deleteParkingDialogOpen,
    requiredFieldsMissingOpen,
    tagsDialogOpen,
    altNamesDialogOpen,
    versionsDialogOpen,

    handleOpenSaveDialog,
    handleCloseSaveDialog,
    handleSave,
    handleAllowUserToGoBack,
    handleGoBack,
    handleCancelGoBack,
    handleOpenUndoDialog,
    handleCloseUndoDialog,
    handleUndo,
    handleOpenTerminateDialog,
    handleCloseTerminateDialog,
    handleTerminate,
    handleCloseDeleteQuayDialog,
    handleConfirmDeleteQuay,
    pendingDeleteQuayId,
    handleCloseDeleteParkingDialog,
    handleConfirmDeleteParking,
    handleOpenRequiredFieldsMissing,
    handleCloseRequiredFieldsMissing,
    handleOpenTagsDialog,
    handleCloseTagsDialog,
    handleOpenAltNamesDialog,
    handleCloseAltNamesDialog,
    handleOpenVersionsDialog: handleOpenVersionsDialogWithFetch,
    handleCloseVersionsDialog,

    handleNameChange,
    handleDescriptionChange,
    handleTypeChange,
    handleSubmodeChange,
    handleWeightingChange,
    handleAddTag,
    handleGetTags,
    handleRemoveTag,
    handleFindTagByName,

    handleDeleteQuay,
    handleQuayPublicCodeChange,
    handleQuayPrivateCodeChange,
    handleQuayDescriptionChange,
    handleQuayCompassBearingChange,

    handleDeleteParking,
    handleParkingNameChange,
    handleParkingTypeChange,
    handleParkingCapacityChange,
  };
};
